import { ref } from 'vue'
import { storeToRefs } from 'pinia'

import { useUserStore } from '@/stores/userStore'

import useBlProviders from '@/composables/providers/useBlProvider'

// BASE
import useLegalEntities from '@/composables/data/useLegalEntities'
import useClusters from '@/composables/data/useClusters'

// PERSONS
import useStaff from '@/composables/data/useStaff'
import useStaffGroups from '@/composables/data/useStaffGroups'
import useStaffRoles from '@/composables/data/useStaffRoles'
import useStaffDepartments from '@/composables/data/useStaffDepartments'
import useCounterparties from '@/composables/data/useCounterparties.js'
import useCounterpartiesGroups from '@/composables/data/useCounterpartiesGroups.js'

// FIELDS
import useFields from '@/composables/data/useFields.js'
import useFieldsTags from '@/composables/data/useFieldsTags.js'

// LANDS
import useLandActions from '@/composables/data/useLandActions.js'
import useLandCategories from '@/composables/data/useLandCategories.js'
import useLandOwnershipTypes from '@/composables/data/useLandOwnershipTypes.js'
import useLandPurposes from '@/composables/data/useLandPurposes.js'

import useWeightStations from '@/composables/data/useWeightStations.js'
import useInfrastructure from '@/composables/data/useInfrastructures'
import useMaterials from '@/composables/data/useMaterials'
import useWorks from '@/composables/data/useWorks'
import useWorksCategories from './useWorksCategories'
import useWorksShifts from '@/composables/data/useWorksShifts.js'
import useWorkReportMeasures from '@/composables/data/useWorkReportMeasures'
import useWorkReportLinks from '@/composables/data/useWorkReportLinks.js'
import useWorkReportPhotos from '@/composables/data/useWorkReportPhotos.js'
import useWorkReportDocuments from '@/composables/data/useWorkReportDocuments.js'
import useRegulationTags from '@/composables/data/useRegulationTags.js'
import useRegulationsStageTags from '@/composables/data/useRegulationsStageTags.js'
import useIncidentTags from '@/composables/data/useIncidentTags.js'
import useDevices from '@/composables/data/useDevices'
import useMachinesGroups from '@/composables/data/useMachinesGroups.js'
import useEquipmentGroups from '@/composables/data/useEquipmentGroups.js'
import useLogisticRouteTags from '@/composables/data/useLogisticRouteTags.js'
import useFinanceCategories from '@/composables/data/useFinanceCategories.js'
import useFinanceGroups from '@/composables/data/useFinanceGroups.js'
import useFinanceTags from '@/composables/data/useFinanceTags.js'

import DataTransferService from '@/services/dataTransferService'
import useMaterialsCategories from './useMaterialsCategories'
import useMaterialsTags from './useMaterialsTags'
import useRegulations from '@/composables/data/useRegulations'

const dataTransferService = DataTransferService()

export default function useCashedData() {
	const { getCurrentVersions } = useBlProviders()
	const userStore = useUserStore()
	const { selectedCompanyId } = storeToRefs(userStore)

	const isLoadingCashedData = ref(false)

	const blParams = {
		showErrorNotificationForLoadingAllEntities: false,
		useLocalRefs: false,
	}

	// BASE
	const { getFulledLegalEntities } = useLegalEntities(blParams)
	const { getFullFieldClusters } = useClusters(blParams)

	// PERSONS
	const { getFulledStaff } = useStaff(blParams)
	const { getStaffGroups } = useStaffGroups(blParams)
	const { getStaffRoles } = useStaffRoles(blParams)
	const { getStaffDepartments } = useStaffDepartments(blParams)
	const { getFulledCounterparties } = useCounterparties(blParams)
	const { getCounterpartiesGroups } = useCounterpartiesGroups(blParams)

	// FIELDS
	const { getFullFields } = useFields(blParams)
	const { getFieldsTags } = useFieldsTags(blParams)

	// LANDS
	const { getLandActions } = useLandActions(blParams)
	const { getLandCategories } = useLandCategories(blParams)
	const { getLandOwnershipTypes } = useLandOwnershipTypes(blParams)
	const { getLandPurposes } = useLandPurposes(blParams)

	const { getInfrastructures } = useInfrastructure(blParams)

	const { getWeightStations } = useWeightStations(blParams)

	const { getFulledMaterials } = useMaterials(blParams)
	const { getMaterialsCategories } = useMaterialsCategories(blParams)
	const { getMaterialsTags } = useMaterialsTags(blParams)

	const { getWorks } = useWorks(blParams)

	const { getWorksCategories } = useWorksCategories(blParams)

	const { getWorksShifts } = useWorksShifts(blParams)

	const { getWorkReportMeasures } = useWorkReportMeasures(blParams)

	const { getWorkReportLinks } = useWorkReportLinks(blParams)

	const { getWorkReportPhotos } = useWorkReportPhotos(blParams)

	const { getWorkReportDocuments } = useWorkReportDocuments(blParams)

	const { getRegulationTags } = useRegulationTags(blParams)

	const { getRegulationsStageTags } = useRegulationsStageTags(blParams)

	const { getIncidentTags } = useIncidentTags(blParams)

	const { getFulledDevices } = useDevices(blParams)

	const { getFinanceCategories } = useFinanceCategories(blParams)

	const { getFinanceGroups } = useFinanceGroups(blParams)

	const { getFinanceTags } = useFinanceTags(blParams)

	const { getMachinesGroups } = useMachinesGroups(blParams)

	const { getEquipmentGroups } = useEquipmentGroups(blParams)

	const { getLogisticRouteTags } = useLogisticRouteTags(blParams)

	const { getRegulations } = useRegulations(blParams)

	const updateCacheData = async () => {
		isLoadingCashedData.value = true
		/* const versions =  */ await getCurrentVersions(selectedCompanyId.value)

		/* Когда делаем рефактор кешируемой сущности необходимо их cacheDtMethodsByKeys вынести и добавить bl метод получения всех в cacheBlMethods */
		const cacheDtMethodsByKeys = {
			// не рефакторить
			[`machines--${selectedCompanyId.value}`]: dataTransferService.machines.getAvailableMachines,

			// не рефакторить
			[`equipment--${selectedCompanyId.value}`]:
				dataTransferService.equipment.getAvailableEquipment,

			[`infrastructure_types--${selectedCompanyId.value}`]:
				dataTransferService.infrastructureTypes.getAvailableInfrastructureTypes,

			[`infrastructures_tags--${selectedCompanyId.value}`]:
				dataTransferService.infrastructureTags.getInfrastructureTags,

			[`identity_cards--${selectedCompanyId.value}`]:
				dataTransferService.identityCards.getFullIdentityCards,

			[`works_materials_consumption_norms--${selectedCompanyId.value}`]:
				dataTransferService.worksMaterialConsumptionRateNorms.getWorksMaterialConsumptionRateNorms,

			[`works_productivity_norms--${selectedCompanyId.value}`]:
				dataTransferService.worksProductivityNorms.getWorksProductivityNorms,

			[`works_quality_norms--${selectedCompanyId.value}`]:
				dataTransferService.worksQualityNorms.getWorksQualityNorms,

			[`works_staff_payment_rates_norms--${selectedCompanyId.value}`]:
				dataTransferService.worksStaffPaymentRatesNorms.getWorksStaffPaymentRatesNorms,

			[`crops--${selectedCompanyId.value}`]: dataTransferService.crops.getCrops,

			[`works_service_payment_rates_norms--${selectedCompanyId.value}`]:
				dataTransferService.worksServicePaymentRateNorms.getWorksServicePaymentRateNorms,

			[`notifications--${selectedCompanyId.value}`]:
				dataTransferService.notifications.getNotificationsUnreadCount,

			[`fuel_stations--${selectedCompanyId.value}`]:
				dataTransferService.fuelStations.getFuelStations,

			//LANDS
			[`lands--${selectedCompanyId.value}`]: dataTransferService.lands.getLands,

			[`land_cadastral_cases--${selectedCompanyId.value}`]:
				dataTransferService.landsCadastralCases.getLandsCadastralCases,
		}

		const cacheBlMethods = [
			getFulledLegalEntities,
			getFullFieldClusters,

			getFulledStaff,
			getStaffGroups,
			getStaffRoles,
			getStaffDepartments,
			getFulledCounterparties,
			getCounterpartiesGroups,

			getFullFields,
			getFieldsTags,

			getLandActions,
			getLandCategories,
			getLandOwnershipTypes,
			getLandPurposes,

			getInfrastructures,
			getWeightStations,
			getFulledMaterials,
			getWorks,
			getWorkReportMeasures,
			getWorkReportLinks,
			getWorkReportPhotos,
			getWorkReportDocuments,
			getRegulationTags,
			getRegulationsStageTags,
			getIncidentTags,
			getFulledDevices,
			getWorksShifts,
			getWorksCategories,
			getMachinesGroups,
			getEquipmentGroups,
			getLogisticRouteTags,
			getFinanceCategories,
			getFinanceGroups,
			getFinanceTags,
			getMaterialsCategories,
			getMaterialsTags,
			getRegulations,
		]

		// eslint-disable-next-line no-unused-vars
		const requests = Object.entries(cacheDtMethodsByKeys).map(([key, requestMethod]) => {
			return requestMethod(selectedCompanyId.value /* , [key, versions[key]] */)
		})

		const blRequests = cacheBlMethods.map((requestMethod) => {
			return requestMethod()
		})

		try {
			await Promise.all([...requests, ...blRequests])
			isLoadingCashedData.value = false
			return true
		} catch {
			isLoadingCashedData.value = false
			return false
		}
	}

	return {
		isLoadingCashedData,
		updateCacheData,
	}
}
